'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/contexts/auth-context';
import { authApi } from '@/lib/api/auth';
import { Sidebar } from '@/components/layout/sidebar';
import { Header } from '@/components/layout/header';
import { NoTenantDialog } from '@/components/no-tenant-dialog';
import { SharedLinkClaimRedeemer } from '@/components/shared-link-claim-redeemer';
import { AssistantRoot } from '@/components/assistant/assistant-root';

export default function DashboardLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user, loading } = useAuth();
  const router = useRouter();

  // Fetch user tenants to check if they have any
  const { data: tenantsData, isLoading: tenantsLoading } = useQuery({
    queryKey: ['user-tenants'],
    queryFn: () => authApi.getTenants(),
    enabled: !!user, // Only fetch when user is logged in
  });

  useEffect(() => {
    if (!loading && !user) {
      if (window.location.pathname !== '/auth/login') {
        sessionStorage.setItem(
          'post_login_return',
          `${window.location.pathname}${window.location.search}`
        );
      }
      router.push('/auth/login');
    }
  }, [user, loading, router]);

  if (loading || (user && tenantsLoading)) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="h-32 w-32 animate-spin rounded-full border-b-2 border-t-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  // Check if user has no tenants
  const hasNoTenants = tenantsData && tenantsData.tenants.length === 0;

  return (
    <div className="flex h-screen overflow-hidden">
      <SharedLinkClaimRedeemer />
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden">
        <Header />
        {/* Top padding stays at p-4 (16px) on every breakpoint so main's content
            starts at the same y as the sidebar's, which sits under an h-16 logo
            block with the same p-4. Both columns then begin at 80px, putting a
            page's h1 (line-height 36px) and the tenant switcher (h-9 = 36px) on
            one line. Widening this to sm:p-6 drops the h1 8px below the switcher. */}
        <main className="flex-1 overflow-y-auto bg-background p-4 sm:px-6 sm:pb-6">
          {children}
        </main>
      </div>

      {/* Mandatory dialog for users with no tenants */}
      <NoTenantDialog open={hasNoTenants ?? false} />

      <AssistantRoot />
    </div>
  );
}
